import { useState, useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { XMarkIcon, ChevronDownIcon, TagIcon } from '@heroicons/react/24/outline'
import { 
  cachedSearchKeywords, 
  cachedGetPopularKeywords, 
  formatKeywordForDisplay, 
  filterSelectedKeywords,
  debounce
} from '../../utils/keywordsHelper'

function KeywordsAutocomplete({ selectedKeywords, onKeywordsChange, disabled = false }) {
  const { t } = useTranslation()
  const [searchQuery, setSearchQuery] = useState('')
  const [searchResults, setSearchResults] = useState([])
  const [popularKeywords, setPopularKeywords] = useState([])
  const [isOpen, setIsOpen] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [focusedIndex, setFocusedIndex] = useState(-1)
  const [showPopular, setShowPopular] = useState(true)
  
  const inputRef = useRef(null)
  const containerRef = useRef(null)
  const resultsRef = useRef(null)

  // Load popular keywords on component mount
  useEffect(() => {
    const loadPopularKeywords = async () => {
      try {
        const keywords = await cachedGetPopularKeywords(50)
        const formattedKeywords = keywords.map(formatKeywordForDisplay)
        setPopularKeywords(formattedKeywords)
      } catch (error) {
        console.error('Error loading popular keywords:', error)
      }
    }

    loadPopularKeywords()
  }, [])

  // Debounced search function
  const debouncedSearch = debounce(async (query) => {
    if (!query.trim()) {
      setSearchResults([])
      setShowPopular(true)
      setIsLoading(false)
      return
    }

    try {
      setIsLoading(true)
      setShowPopular(false)
      
      const results = await cachedSearchKeywords(query, 20)
      const formattedResults = results.map(formatKeywordForDisplay)
      const filteredResults = filterSelectedKeywords(formattedResults, selectedKeywords)
      
      setSearchResults(filteredResults)
      setIsLoading(false)
    } catch (error) {
      console.error('Error searching keywords:', error)
      setSearchResults([])
      setShowPopular(true) // Show popular keywords if search fails
      setIsLoading(false)
    }
  }, 600)

  // Handle search input changes
  const handleSearchChange = (event) => {
    const query = event.target.value
    setSearchQuery(query)
    setFocusedIndex(-1)
    
    if (!query.trim()) {
      setSearchResults([])
      setShowPopular(true)
      setIsLoading(false)
    } else {
      setIsLoading(true)
      debouncedSearch(query)
    }
  }

  // Get current display list (search results or popular keywords)
  const getCurrentList = () => {
    // If user is typing and we have search results, show them
    if (searchQuery.trim() && searchResults.length > 0) {
      return searchResults
    }
    
    // If user is typing but we're loading or have no results yet, show empty array
    // (this will show "searching..." or "no results" message)
    if (searchQuery.trim() && !showPopular) {
      return []
    }
    
    // Otherwise show popular keywords (filtered to exclude selected ones)
    const filtered = filterSelectedKeywords(popularKeywords, selectedKeywords)
    return filtered
  }

  // Handle keyword selection
  const handleKeywordSelect = (keyword) => {
    const newSelectedKeywords = [...selectedKeywords, keyword]
    onKeywordsChange(newSelectedKeywords)
    setSearchQuery('')
    setSearchResults([])
    setShowPopular(true)
    setFocusedIndex(-1)
    inputRef.current?.focus()
  }

  // Handle keyword removal
  const handleKeywordRemove = (keywordToRemove) => {
    const newSelectedKeywords = selectedKeywords.filter(k => k.id !== keywordToRemove.id)
    onKeywordsChange(newSelectedKeywords)
  }

  // Handle input focus
  const handleInputFocus = () => {
    setIsOpen(true)
  }

  // Handle input blur with delay to allow for clicks
  const handleInputBlur = () => {
    setTimeout(() => {
      setIsOpen(false)
      setFocusedIndex(-1)
    }, 200)
  }

  // Handle keyboard navigation
  const handleKeyDown = (event) => {
    const currentList = getCurrentList()
    
    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault()
        setFocusedIndex(prev => 
          prev < currentList.length - 1 ? prev + 1 : 0
        )
        break
      
      case 'ArrowUp':
        event.preventDefault()
        setFocusedIndex(prev => 
          prev > 0 ? prev - 1 : currentList.length - 1
        )
        break
      
      case 'Enter':
        event.preventDefault()
        if (focusedIndex >= 0 && focusedIndex < currentList.length) {
          handleKeywordSelect(currentList[focusedIndex])
        }
        break
      
      case 'Escape':
        event.preventDefault()
        setIsOpen(false)
        setFocusedIndex(-1)
        inputRef.current?.blur()
        break
        
      default:
        break
    }
  }

  // Scroll focused item into view
  useEffect(() => {
    if (focusedIndex >= 0 && resultsRef.current) {
      const focusedElement = resultsRef.current.children[focusedIndex]
      if (focusedElement) {
        focusedElement.scrollIntoView({
          block: 'nearest',
          behavior: 'smooth'
        })
      }
    }
  }, [focusedIndex])

  const currentList = getCurrentList()
  const hasResults = currentList.length > 0

  return (
    <div className="space-y-2">
      {/* Label */}
      <label className="block text-sm font-medium text-gray-700">
        {t('articleBibliography.search.keywords.label')}
      </label>

      {/* Selected Keywords Pills */}
      {selectedKeywords.length > 0 && (
        <div className="flex flex-wrap gap-2 p-2 bg-gray-50 rounded-md">
          {selectedKeywords.map((keyword) => (
            <span
              key={keyword.id}
              className="inline-flex items-center gap-1 px-2 py-1 bg-black text-white text-sm rounded-md"
            >
              <TagIcon className="h-3 w-3" />
              <span className="max-w-48 truncate">{keyword.displayName}</span>
              {!disabled && (
                <button
                  type="button"
                  onClick={() => handleKeywordRemove(keyword)}
                  className="ml-1 hover:bg-gray-700 rounded p-0.5 transition-colors"
                  aria-label={t('articleBibliography.search.keywords.remove', { keyword: keyword.displayName })}
                >
                  <XMarkIcon className="h-3 w-3" />
                </button>
              )}
            </span>
          ))}
        </div>
      )}

      {/* Search Input Container */}
      <div ref={containerRef} className="relative">
        <div className="relative">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <TagIcon className="h-5 w-5 text-gray-400" />
          </div>
          <input
            ref={inputRef}
            type="text"
            value={searchQuery}
            onChange={handleSearchChange}
            onFocus={handleInputFocus}
            onBlur={handleInputBlur}
            onKeyDown={handleKeyDown}
            className="block w-full pl-10 pr-10 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-black focus:border-black"
            placeholder={t('articleBibliography.search.keywords.placeholder')}
            disabled={disabled}
            aria-expanded={isOpen}
            aria-haspopup="listbox"
            aria-autocomplete="list"
          />
          <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
            <ChevronDownIcon 
              className={`h-5 w-5 text-gray-400 transition-transform ${isOpen ? 'rotate-180' : ''}`} 
            />
          </div>
        </div>

        {/* Dropdown Results */}
        {isOpen && (
          <div className="absolute z-10 mt-1 w-full bg-white shadow-lg max-h-60 rounded-md py-1 text-base ring-1 ring-black ring-opacity-5 overflow-auto focus:outline-none">
            {isLoading && (
              <div className="px-4 py-3 text-gray-500 text-sm flex items-center">
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-400 mr-2"></div>
                {t('articleBibliography.search.keywords.searching')}
              </div>
            )}

            {!isLoading && !hasResults && searchQuery && (
              <div className="px-4 py-3 text-gray-500 text-sm">
                {t('articleBibliography.search.keywords.noResults')}
              </div>
            )}

            {!isLoading && showPopular && hasResults && (
              <div className="px-4 py-2 text-xs font-medium text-gray-500 bg-gray-50 border-b">
                {t('articleBibliography.search.keywords.popularKeywords')}
              </div>
            )}

            {!isLoading && hasResults && (
              <div ref={resultsRef} role="listbox">
                {currentList.map((keyword, index) => (
                  <button
                    key={keyword.id}
                    type="button"
                    className={`w-full px-4 py-2 text-left hover:bg-gray-100 flex items-center justify-between ${
                      index === focusedIndex ? 'bg-gray-100' : ''
                    }`}
                    onClick={() => handleKeywordSelect(keyword)}
                    role="option"
                    aria-selected={index === focusedIndex}
                  >
                    <div className="flex flex-col">
                      <span className="text-sm font-medium text-gray-900 truncate">
                        {keyword.displayName}
                      </span>
                    </div>
                    <TagIcon className="h-4 w-4 text-gray-400 flex-shrink-0 ml-2" />
                  </button>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Helper Text */}
      <p className="text-xs text-gray-500">
        {selectedKeywords.length === 0 
          ? t('articleBibliography.search.keywords.helpText')
          : t('articleBibliography.search.keywords.selectedCount', { count: selectedKeywords.length })
        }
      </p>
    </div>
  )
}

export default KeywordsAutocomplete